import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Wifi, Network, ShieldCheck,
  DoorOpen, Monitor, Phone, Cloud, Headphones,
  CheckCircle2, GraduationCap
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../i18n";

const solutionIcons = [
  Network, Wifi, DoorOpen, ShieldCheck,
  Monitor, Phone, Cloud, Headphones
];

export default function Educacion() {
  const { t } = useI18n();
  const solutions = solutionIcons.map((icon, index) => ({
    icon,
    title: t(`education.solution${index + 1}Title`),
    desc: t(`education.solution${index + 1}Desc`)
  }));

  const projectImages = [
    "/images/educacion/educacion-redes.webp",
    "/images/educacion/educacion-ciberseguridad.webp",
    "/images/educacion/educacion-accesos-aulas.webp"
  ];

  const projects = [1, 2, 3].map((number) => ({
    number: String(number).padStart(2, "0"),
    image: projectImages[number - 1],
    title: t(`education.project${number}Title`),
    desc: t(`education.project${number}Desc`)
  }));

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      <Navbar />

      <main>
        <section className="relative isolate overflow-hidden pt-36 pb-24 md:pt-48 md:pb-32">
          {/* Fotografía del aula tecnológica */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 bg-cover bg-[position:62%_center] opacity-40 md:bg-[position:center_center] md:opacity-65"
            style={{
              backgroundImage: "url('/images/educacion/hero.png')"
            }}
          />

          {/* Degradado para integrar la imagen con la identidad NIDA */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30"
          />

          {/* Transición suave hacia la siguiente sección */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-32 bg-gradient-to-t from-[#050505] to-transparent"
          />
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 right-0 h-[650px] w-[650px] rounded-full bg-brand/15 blur-[130px]" />
            <div className="absolute inset-0 grid-bg opacity-20" />
          </div>

          <div className="container-page relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              <span className="eyebrow">
                <GraduationCap className="h-4 w-4" />
                {t("education.heroEyebrow")}
              </span>

              <h1 className="mt-8 text-5xl md:text-7xl font-semibold tracking-tighter leading-tight">
                {t("education.heroTitle1")}
                <br />
                <span className="gradient-text">{t("education.heroTitle2")}</span>
              </h1>

              <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-white/60">
                {t("education.heroDesc")}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a href="#contacto-educacion" className="btn-primary">
                  {t("education.cta")}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <a href="#soluciones-educacion" className="btn-ghost">
                  {t("education.discover")}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="section-pad border-t border-white/[0.06]">
          <div className="container-page">
            <span className="eyebrow">{t("education.challengeEyebrow")}</span>
            <h2 className="mt-6 max-w-3xl text-4xl md:text-5xl font-semibold">
              {t("education.challengeTitle")}
            </h2>
            <p className="mt-6 max-w-3xl text-white/60 text-lg leading-relaxed">
              {t("education.challengeDesc1")}{" "}
              {t("education.challengeDesc2")}
            </p>
          </div>
        </section>

        <section
          id="soluciones-educacion"
          className="section-pad border-t border-white/[0.06] scroll-mt-24"
        >
          <div className="container-page">
            <span className="eyebrow">{t("education.solutionsEyebrow")}</span>
            <h2 className="mt-6 text-4xl md:text-5xl font-semibold">
              {t("education.solutionsTitle1")}
              <br />
              <span className="gradient-text">{t("education.solutionsTitle2")}</span>
            </h2>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {solutions.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="card-surface p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-brand/20 bg-brand/10 text-brand-glow">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-7 text-xl font-semibold">{title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-white/60">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-pad border-t border-white/[0.06]">
          <div className="container-page">
            <span className="eyebrow">{t("education.projectsEyebrow")}</span>
            <h2 className="mt-6 text-4xl md:text-5xl font-semibold">
              {t("education.projectsTitle")}
            </h2>
            <p className="mt-5 max-w-2xl text-white/60">
              {t("education.projectsDesc")}
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
              {projects.map((project) => (
                  <div key={project.number} className="card-surface p-8">
              <div className="mb-6 overflow-hidden rounded-xl border border-white/10 bg-white/5">
                    <img
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      decoding="async"
                      className="h-52 w-full object-cover object-center transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <span className="font-mono text-sm text-brand-glow">
                    {project.number}
                  </span>
                  <h3 className="mt-4 text-2xl font-semibold">
                    {project.title}
                  </h3>
                  <p className="mt-5 text-white/60 leading-relaxed">
                    {project.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-pad border-t border-white/[0.06]">
          <div className="container-page grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <span className="eyebrow">{t("education.methodEyebrow")}</span>
              <h2 className="mt-6 text-4xl md:text-5xl font-semibold">
                {t("education.methodTitle")}
              </h2>
            </div>

            <div className="space-y-7">
              {[1, 2, 3, 4].map((number) => [
              String(number).padStart(2, "0"),
              t(`education.method${number}Title`),
              t(`education.method${number}Desc`)
            ]).map(([n, title, desc]) => (
                <div key={n} className="flex gap-5 border-b border-white/10 pb-7">
                  <span className="font-mono text-brand-glow">{n}</span>
                  <div>
                    <h3 className="text-xl font-semibold">{title}</h3>
                    <p className="mt-2 text-white/60">{desc}</p>
                  </div>
                  <CheckCircle2 className="ml-auto h-5 w-5 shrink-0 text-brand-glow" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="contacto-educacion"
          className="section-pad border-t border-white/[0.06] scroll-mt-24"
        >
          <div className="container-page">
            <div className="card-surface p-9 md:p-14 text-center">
              <span className="eyebrow">{t("education.contactEyebrow")}</span>
              <h2 className="mt-7 text-4xl md:text-5xl font-semibold">
                {t("education.contactTitle")}
              </h2>
              <p className="mt-6 mx-auto max-w-xl text-white/60">
                {t("education.contactDesc")}
              </p>
              <Link
                to="/?sector=educacion#contact"
                className="btn-primary mt-9"
              >
                {t("education.cta")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
